import { useState } from "react";
import { Facebook, Instagram, Loader2, Mail, Music2, Send, AtSign, Globe } from "lucide-react";
import { toast } from "sonner";
import LegalShell, { Section, MailLink } from "@/components/LegalShell";
import { api } from "@/lib/apiClient";

const input = "w-full bg-[#04060B] border border-[#D4AF37]/20 rounded-xl px-3 py-2.5 text-base text-starlight placeholder:text-[#5f6b7d] focus:outline-none focus:border-[#D4AF37]/60";
const SOCIAL = [
  [Facebook, "Facebook", "Astra Horoscopy", "https://www.facebook.com/search/top?q=Astra%20Horoscopy", "facebook"],
  [AtSign, "Threads", "@astrahoroscopy", "https://www.threads.net/@astrahoroscopy", "threads"],
  [Instagram, "Instagram", "@astrahoroscopy", "https://www.instagram.com/astrahoroscopy/", "instagram"],
  [Music2, "TikTok", "@astra.horoscopy", "https://www.tiktok.com/@astra.horoscopy", "tiktok"],
];

function ContactForm() {
  const [f, setF] = useState({ name: "", email: "", subject: "", message: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/public/contact", f);
      setDone(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Please check your details and try again.");
    } finally { setBusy(false); }
  };
  if (done) return <p data-testid="contact-form-success" className="bg-[#0F1523] border border-[#D4AF37]/30 rounded-2xl p-5 text-[#E8DCC4]">Thank you, your message is on its way. We reply within 24-48 hours.</p>;
  return (
    <form onSubmit={send} data-testid="contact-form" className="bg-[#0F1523]/90 border border-[#D4AF37]/25 rounded-2xl p-5 space-y-3">
      <input data-testid="contact-name" required maxLength={80} className={input} placeholder="Your name" value={f.name} onChange={set("name")} />
      <input data-testid="contact-email" required type="email" className={input} placeholder="Your email" value={f.email} onChange={set("email")} />
      <input data-testid="contact-subject" maxLength={120} className={input} placeholder="Subject (e.g. Editorial)" value={f.subject} onChange={set("subject")} />
      <textarea data-testid="contact-message" required minLength={5} maxLength={3000} rows={5} className={input} placeholder="Your message" value={f.message} onChange={set("message")} />
      <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" value={f.website} onChange={set("website")} />
      <button data-testid="contact-submit" disabled={busy}
        className="w-full flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium py-3 rounded-full transition-colors disabled:opacity-50">
        {busy ? <Loader2 className="animate-spin" size={17} /> : <><Send size={16} /> Send message</>}
      </button>
    </form>
  );
}

export default function Contact() {
  return (
    <LegalShell kicker="Contact" title="Contact Astra" testId="contact-page" path="/contact" docTitle="Contact Us · Astra">
      <p>We'd love to hear from you.</p>
      <Section title="Email">
        <p className="flex items-center gap-2"><Mail size={16} className="gold-text" /> <MailLink /></p>
        <p className="text-sm text-[#A7B0C0]">We reply within 24-48 hours.</p>
      </Section>
      <Section title="Social">
        {SOCIAL.map(([Icon, label, handle, url, id]) => (
          <p key={id} className="flex items-center gap-2"><Icon size={16} className="gold-text" /> {label}:{" "}
            <a href={url} target="_blank" rel="noopener noreferrer" data-testid={`contact-social-${id}`} className="gold-text underline underline-offset-4">{handle}</a></p>
        ))}
      </Section>
      <Section title="Website">
        <p className="flex items-center gap-2"><Globe size={16} className="gold-text" /> <a href="https://astralogico.com" data-testid="contact-website" className="gold-text underline underline-offset-4">astralogico.com</a></p>
      </Section>
      <p>For press, partnerships, or corrections, please email <MailLink subject="Editorial" /> with subject "Editorial".</p>
      <Section title="Send us a message">
        <ContactForm />
      </Section>
    </LegalShell>
  );
}
